import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ListItem, Stack, styled, Typography } from '@mui/material'
import type { FC, MouseEvent } from 'react'
import { useMemo } from 'react'
import type { To } from 'react-router'

import { COLORS, ICONS, LinkUnstyled, remUnit, unit } from '@hcr/ui'
import { isNotUndefined } from '@hcr/utils'

import { useLastPath } from '../../../../../contexts'
import { Path } from '../../../../../models'
import { isServicesPath, to } from '../../../../../utils'
import { BookingListDescription } from '../../../../booking/booking/common'
import type { BookingListPreviewProps } from '../../../../common'

export const BookingListPreview: FC<BookingListPreviewProps> = ({
  dateRange,
  destinationId,
  destinationName,
  isActive,
  onClick,
  totalTickets,
}) => {
  const lastPath = useLastPath()

  const backNavigationPath = useMemo((): To => {
    if (isNotUndefined(destinationId) && isServicesPath(lastPath.root)) {
      return to([Path.Services, { destinationId }])
    }

    return lastPath.root
  }, [destinationId, lastPath.root])

  const handleClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return
    }

    onClick()
  }

  return (
    <ListItem disablePadding>
      <BookingListLink
        to={backNavigationPath}
        aria-current={isActive ? 'true' : undefined}
        sx={{
          backgroundColor: isActive ? COLORS.hiekka[500] : COLORS.vaalea['60%'],
          color: isActive ? COLORS.white : COLORS.black,
          borderLeft: isActive ? `${unit(1)} solid ${COLORS.burgundi[500]}` : 'none',
        }}
        onClick={handleClick}
      >
        <Stack direction='row' alignItems='center' justifyContent='space-between' gap={unit(4)} width={'100%'}>
          <Stack direction='column' gap={unit(1)}>
            <Typography component='h2' color={COLORS.burgundi[500]} variant='displayS'>
              {destinationName}
            </Typography>
            <BookingListDescription dateRange={dateRange} totalTickets={totalTickets} variant='bodyM' />
          </Stack>
          <Icon color={COLORS.black} icon={ICONS.fasChevronRight} aria-hidden='true' />
        </Stack>
      </BookingListLink>
    </ListItem>
  )
}

const BookingListLink = styled(LinkUnstyled)`
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: ${unit(4)};
  text-align: left;
  border-top: thin solid ${COLORS.vaalea[600]};
  padding: ${unit(4)} ${unit(9)} ${unit(5)} ${unit(7)};
  cursor: pointer;
`

const Icon = styled(FontAwesomeIcon)`
  align-self: flex-start;
  padding-top: ${unit(2.25)};
  font-size: ${remUnit(5)};
`
